// -------------------------------
// PROMOTION INTERFACES
// -------------------------------
export interface PendingPromotion {
    employeeId?: number;
    employeeName?: string;

    promotionTo?: number;
    promotionToLabel?: string;

    promotionDate?: string;

    newGrade?: number;
    newGradeLabel?: string;

    newDepartment?: number;
    newDepartmentLabel?: string;

    newManager?: number;
    newManagerLabel?: string;

    relocate?: boolean;
    address?: string;

    experienceLevel?: string;
}

export interface PendingDeletePromotion {
    awaitingName?: boolean;
}

// -------------------------------
// CREATE PROMOTION — START
// -------------------------------
export const startCreatePromotionFlow = (
    setPendingPromotion: (data: PendingPromotion | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    setPendingPromotion({});
    pushAIChat("Sure! Which employee are you promoting?");
    speak("Which employee are you promoting?");

    window.dispatchEvent(
        new CustomEvent("ai-suggest-options", {
            detail: { type: "employee-promotion" }
        })
    );
};

// -------------------------------
// CREATE PROMOTION — PROCESS
// -------------------------------
export const processPendingPromotion = (
    text: string,
    pending: PendingPromotion,
    setPendingPromotion: (data: PendingPromotion | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {

    // 1️⃣ Employee
    if (!pending.employeeId) {
        pushAIChat("Please select an employee.");
        speak("Please select an employee.");
        return;
    }

    // 2️⃣ Promotion To (Designation)
    if (!pending.promotionTo) {
        pushAIChat("What is the new designation?");
        speak("What is the new designation?");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "designation" }
            })
        );
        return;
    }

    // 3️⃣ Promotion Date
    if (!pending.promotionDate) {
        const cleanDate = text.trim();

        if (!cleanDate) {
            pushAIChat("Please provide a valid promotion date.");
            speak("Please provide a valid promotion date.");
            return;
        }

        setPendingPromotion({ ...pending, promotionDate: cleanDate });

        // 👉 MOVE FORWARD
        pushAIChat("Select the new grade.");
        speak("Select the new grade.");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "payscale" }
            })
        );
        return;
    }

    // 5️⃣ New Department
    if (!pending.newDepartment) {
        pushAIChat("Which is the new department?");
        speak("Which is the new department?");

        window.dispatchEvent(
            new CustomEvent("ai-suggest-options", {
                detail: { type: "department-promotion" }
            })
        );
        return;
    }

    // 7️⃣ Relocation
    if (pending.relocate === undefined) {
        const lower = text.toLowerCase();

        if (lower.includes("yes")) {
            setPendingPromotion({ ...pending, relocate: true });
            pushAIChat("What is the new job location?");
            speak("What is the new job location?");
            return;
        }

        if (lower.includes("no")) {
            setPendingPromotion({ ...pending, relocate: false });
            finalizePromotion(pending, setPendingPromotion, pushAIChat, speak);
            return;
        }

        pushAIChat("Is the employee relocating? Yes or No?");
        speak("Is the employee relocating?");
        return;
    }

    // 8️⃣ Address (Only if relocating)
    if (pending.relocate && !pending.address) {
        setPendingPromotion({ ...pending, address: text });
        finalizePromotion({ ...pending, address: text }, setPendingPromotion, pushAIChat, speak);
    }
};

const finalizePromotion = (
    pending: PendingPromotion,
    setPendingPromotion: (data: PendingPromotion | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    pushAIChat("Creating promotion now...");
    speak("Creating promotion now.");

    window.dispatchEvent(
        new CustomEvent("ai-create-promotion", {
            detail: {
                Employee: pending.employeeId,
                promotionTo: pending.promotionTo,
                promotionDate: pending.promotionDate,
                newGrade: pending.newGrade,
                newDepartment: pending.newDepartment,
                newManager: pending.newManager,
                relocate: pending.relocate,
                address: pending.address,
                experienceLevel: pending.experienceLevel
            }
        })
    );

    setPendingPromotion(null);
};

// -------------------------------
// DELETE PROMOTION — START
// -------------------------------
export const startDeletePromotionFlow = (
    setPendingDelete: (data: PendingDeletePromotion | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void,
    nameFromIntent?: string
) => {
    if (nameFromIntent) {
        pushAIChat(`Deleting promotion for "${nameFromIntent}"...`);
        speak(`Deleting promotion for ${nameFromIntent}`);

        window.dispatchEvent(
            new CustomEvent("ai-delete-promotion", {
                detail: { name: nameFromIntent }
            })
        );
        return;
    }

    setPendingDelete({ awaitingName: true });
    pushAIChat("Which employee's promotion should be deleted?");
    speak("Which employee's promotion should be deleted?");
};

// -------------------------------
// DELETE PROMOTION — PROCESS
// -------------------------------
export const processPendingDeletePromotion = (
    text: string,
    pending: PendingDeletePromotion,
    setPendingDelete: (data: PendingDeletePromotion | null) => void,
    pushAIChat: (msg: string) => void,
    speak: (msg: string) => void
) => {
    if (!pending.awaitingName) return;

    const cleanName = text.replace(/[^a-zA-Z0-9 ]/g, "").trim();

    if (!cleanName) {
        pushAIChat("Please provide a valid employee name.");
        speak("Please provide a valid employee name.");
        return;
    }

    pushAIChat(`Deleting promotion for "${cleanName}"...`);
    speak(`Deleting promotion for ${cleanName}`);

    window.dispatchEvent(
        new CustomEvent("ai-delete-promotion", {
            detail: { name: cleanName }
        })
    );

    setPendingDelete(null);
};

